import { useCallback, useRef, useState } from 'react';

// Media seek + editor re-render are heavy, so commit at most this often while
// dragging — matching the playback clock keeps scrubbing as light as playback.
const COMMIT_INTERVAL_MS = 100;

/**
 * Click / drag-to-seek for the timeline. The caller supplies `toSec(clientX)`
 * (pointer X → clamped seconds, in its own coordinate space) and attaches the
 * returned `onPointerDown` to the seekable surfaces. Read `scrubSec` for the
 * live playhead position — non-null only mid-gesture.
 *
 * Performance: `scrubSec` updates once per animation frame so the playhead
 * follows the pointer smoothly (and, kept in a small layer, re-renders nothing
 * but the playhead). The real seek (`onSeek`, which moves the video/audio and
 * re-renders the editor) is throttled to `COMMIT_INTERVAL_MS` and always fired
 * once more — exactly — on release.
 *
 * Edge auto-scroll: pass `autoScroll = { getVelocity(clientX), scrollBy(px) }`
 * to keep scrolling (and seeking the newly revealed content) while the pointer
 * is held against a viewport edge. `getVelocity` returns px/frame (0 = idle);
 * as long as it is non-zero the frame loop keeps running even without pointer
 * movement, so the timeline scrolls under a stationary pointer.
 */
const useTimelineSeek = ({ onSeek, toSec, autoScroll } = {}) => {
  const [scrubSec, setScrubSec] = useState(null);
  // One gesture at a time (as in useTimelineDrag). The handler is attached to a
  // container, so it sees every descendant's press bubble up: a second, nested
  // start would register a second listener pair AND capture `userSelect` while
  // it is already 'none' — its release would then leave the page unselectable.
  const activeRef = useRef(false);
  // When the last real seek was committed. A ref, not a per-gesture local, so
  // the throttle is shared with scrubTo below — the two never run at once, and
  // one clock is one thing to reason about.
  const lastCommitRef = useRef(0);

  const onPointerDown = useCallback(
    (event) => {
      if (event.button || !toSec || activeRef.current) return; // primary only
      activeRef.current = true;

      // Stop the drag from selecting/highlighting page text as the pointer
      // sweeps over it: cancel the default press behaviour and suppress
      // selection document-wide until release.
      event.preventDefault();
      const prevUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';

      let rafId = 0;
      let latestX = event.clientX;
      let dragging = true;

      const commit = (sec) => {
        lastCommitRef.current = performance.now();
        onSeek?.(sec);
      };

      const frame = () => {
        rafId = 0;
        // Nudge the viewport if the pointer sits in an edge band; the seek
        // below then lands on whatever content scrolled under the pointer.
        const velocity = autoScroll?.getVelocity(latestX) ?? 0;
        if (velocity) autoScroll.scrollBy(velocity);

        const sec = toSec(latestX);
        setScrubSec(sec); // smooth, isolated visual
        if (performance.now() - lastCommitRef.current >= COMMIT_INTERVAL_MS) {
          commit(sec);
        }

        // Keep the loop alive while auto-scrolling so it continues even when
        // the pointer is held perfectly still against the edge.
        if (dragging && velocity) rafId = window.requestAnimationFrame(frame);
      };

      const scheduleFrame = () => {
        if (!rafId) rafId = window.requestAnimationFrame(frame);
      };

      const onMove = (moveEvent) => {
        latestX = moveEvent.clientX;
        scheduleFrame();
      };

      const onUp = (upEvent) => {
        dragging = false;
        activeRef.current = false;
        if (rafId) window.cancelAnimationFrame(rafId);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.style.userSelect = prevUserSelect; // restore selection
        commit(toSec(upEvent.clientX)); // land on the exact release point
        setScrubSec(null);
      };

      const sec = toSec(event.clientX);
      setScrubSec(sec);
      commit(sec); // a bare click seeks immediately
      // Kick the loop once so a press-and-hold at an edge auto-scrolls even
      // before the first pointermove.
      scheduleFrame();
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [onSeek, toSec, autoScroll],
  );

  // Drive the playhead from something that is NOT the pointer — a clip's trim
  // edge, which lands on the clamped/snapped position rather than under the
  // cursor, so it can't go through the gesture above. Same split as scrubbing:
  // the visual moves on every call, the real seek stays throttled.
  const scrubTo = useCallback(
    (sec) => {
      setScrubSec(sec);
      if (performance.now() - lastCommitRef.current >= COMMIT_INTERVAL_MS) {
        lastCommitRef.current = performance.now();
        onSeek?.(sec);
      }
    },
    [onSeek],
  );

  // Land on the exact release position and hand the playhead back to playback.
  const endScrub = useCallback(
    (sec) => {
      lastCommitRef.current = performance.now();
      onSeek?.(sec);
      setScrubSec(null);
    },
    [onSeek],
  );

  return { onPointerDown, scrubSec, scrubTo, endScrub };
};

export default useTimelineSeek;
